export interface MailPerson {
  name: string | null;
  address: string | null;
}

export interface ThreadSummary {
  id: string;
  subject: string;
  status: "open" | "done";
  participants: MailPerson[];
  messageCount: number;
  lastMessageAt: string;
  last: { fromName: string | null; fromAddress: string | null; snippet: string; mine: boolean } | null;
  awaitingReply: boolean;
  created: number;
  drafts: number;
  /** Rated by AI when the space sorts by importance: 0 can wait … 3 urgent (null: not rated). */
  importance: number | null;
}

export interface CorrespondenceList {
  available: boolean;
  threads: ThreadSummary[];
  address: { email: string; policy: "members" | "anyone"; createdAt: string; held: number; paused: string | null } | null;
  domain: string;
  receiving?: boolean;
  /** "Sort by importance using AI": on for this notebook or project, or why it cannot be (null: it can). */
  sortByImportance: { offered: boolean; on: boolean; unavailable: string | null };
}

export interface MailMessage {
  id: string;
  fromName: string | null;
  fromAddress: string | null;
  fromMember: { userId: string; name: string; me: boolean } | null;
  to: MailPerson[];
  cc: MailPerson[];
  sentAt: string | null;
  dateText: string | null;
  subject: string;
  text: string;
  source: "paste" | "eml" | "msg" | "forward" | "quoted" | "sent";
  auth: { verifiedSender?: boolean; forwardedBy?: MailPerson; acceptedBy?: string } | null;
  hasOriginal: boolean;
  addedBy: string | null;
  createdAt: string;
  attachments: { id: string; filename: string; mime: string; sizeBytes: number }[];
}

export interface MailDraft {
  id: string;
  number: number;
  instructions: string;
  tone: string | null;
  subject: string;
  body: string;
  to: MailPerson[];
  cc: MailPerson[];
  proposalId: string | null;
  basedOnId: string | null;
  by: string | null;
  createdAt: string;
  copiedAt: string | null;
  openedAt: string | null;
  sentAt: string | null;
}

export interface Suggestions {
  manuscripts: { id: string; title: string; status: string; score: number; reason: string | null }[];
  reviewers: { messageId: string; receivedOn: string | null; sets: { label: string; text: string }[] } | null;
  status: { to: string; quote: string; date: string; messageId: string } | null;
  versions: { attachmentId: string; filename: string; messageId: string }[];
  events: { summary: string; date: string; time: string | null; location: string | null; attachmentId: string }[];
  papers: { identifiers: string[]; alreadyInLibrary: number };
  needsReply: boolean;
  place: { workspaceId: string; name: string; kind: string; reasons: string[] } | null;
}

export interface TriageTask {
  key: string;
  title: string;
  description: string;
  sourcePassage: string;
  passageFound: boolean;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeNote: string | null;
  dueDate: string | null;
  dueNote: string | null;
  suggestedDate: { date: string; explanation: string } | null;
  likelyDuplicateOf: { id: string; title: string; status: string } | null;
}

export interface ThreadDetail {
  thread: { id: string; workspaceId: string; subject: string; status: "open" | "done"; participants: MailPerson[]; lastMessageAt: string; createdAt: string };
  messages: MailMessage[];
  drafts: MailDraft[];
  links: { id: string; entityType: string; entityId: string; label: string; by: string | null; createdAt: string }[];
  triage: {
    proposalId: string;
    createdAt: string;
    isDemo: boolean;
    result: { summary: string; needsReply: boolean; replyReason: string; category: string; items: TriageTask[]; dates: { what: string; date: string | null; dateText: string; sourcePassage: string }[] };
  } | null;
  suggestions: Suggestions;
}

export const ckey = {
  list: (ws: string) => ["correspondence", ws] as const,
  thread: (id: string) => ["mail-thread", id] as const,
  address: (ws: string) => ["mail-address", ws] as const,
};

export const personLabel = (p: MailPerson | null | undefined) => (p ? p.name || p.address || "Unknown" : "Unknown");
